import { calculateChart, calculateAstrologyExtras } from './calc/chart.js';
import { GATE_NAMES, CENTER_NAMES, getGateAndLine } from './data/gates.js';
import { CHANNELS, getChannelName } from './data/channels.js';
import { searchCity, getTimezoneFromCoords, getUtcOffsetForDate, getTimezoneDescription } from './geo/geocoding.js';
import './style.css';

let chart1 = null;

document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('birthForm').addEventListener('submit', handleCalculate);
  document.getElementById('toggleComposite')?.addEventListener('click', toggleComposite);
  document.getElementById('calculateComposite')?.addEventListener('click', handleComposite);
  document.getElementById('closeComposite')?.addEventListener('click', () => {
    document.getElementById('compositeSection').style.display = 'none';
  });

  // City autocomplete for main form
  setupCityAutocomplete('birthCity', 'citySuggestions', (result) => {
    document.getElementById('latitude').value = result.lat;
    document.getElementById('longitude').value = result.lon;
    resolveTimezone(result.lat, result.lon, 'geoInfo', 'geoCoords', 'geoTimezone', 'timezone', 'ianaTimezone');
  });

  // City autocomplete for composite forms
  setupCityAutocomplete('compositeBirthCity1', 'compositeCitySuggestions1', (result) => {
    resolveTimezoneSimple(result.lat, result.lon, 'compositeGeoInfo1', 'compositeGeoTz1', 'compositeTimezone1', 'compositeIanaTimezone1');
  });
  setupCityAutocomplete('compositeBirthCity2', 'compositeCitySuggestions2', (result) => {
    resolveTimezoneSimple(result.lat, result.lon, 'compositeGeoInfo2', 'compositeGeoTz2', 'compositeTimezone2', 'compositeIanaTimezone2');
  });

  console.log('Human Design Calculator geladen');
});

/* =========== City Autocomplete =========== */

function setupCityAutocomplete(inputId, suggestionsId, onSelect) {
  const input = document.getElementById(inputId);
  const suggestionsEl = document.getElementById(suggestionsId);
  if (!input || !suggestionsEl) return;

  let debounceTimer = null;
  let currentResults = [];
  let highlightIndex = -1;

  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const query = input.value.trim();

    if (query.length < 2) {
      hideSuggestions();
      return;
    }

    debounceTimer = setTimeout(async () => {
      try {
        suggestionsEl.innerHTML = '<div class="city-loading">Suche…</div>';
        suggestionsEl.classList.add('active');

        currentResults = await searchCity(query);
        highlightIndex = -1;

        if (currentResults.length === 0) {
          suggestionsEl.innerHTML = '<div class="city-loading">Kein Ergebnis gefunden</div>';
          return;
        }

        suggestionsEl.innerHTML = currentResults.map((r, i) => {
          const parts = r.displayName.split(', ');
          const name = parts[0];
          const detail = parts.slice(1, 3).join(', ');
          return `<div class="city-suggestion" data-index="${i}">
            <div class="city-name">${name}</div>
            <div class="city-detail">${detail}</div>
          </div>`;
        }).join('');

        suggestionsEl.querySelectorAll('.city-suggestion').forEach(el => {
          el.addEventListener('click', () => selectResult(parseInt(el.dataset.index)));
        });
      } catch (err) {
        console.warn('City search failed:', err);
        suggestionsEl.innerHTML = '<div class="city-loading">Fehler bei der Suche</div>';
      }
    }, 350);
  });

  // Keyboard navigation
  input.addEventListener('keydown', (e) => {
    if (!suggestionsEl.classList.contains('active') || currentResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlightIndex = Math.min(highlightIndex + 1, currentResults.length - 1);
      updateHighlight();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlightIndex = Math.max(highlightIndex - 1, 0);
      updateHighlight();
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      selectResult(highlightIndex);
    } else if (e.key === 'Escape') {
      hideSuggestions();
    }
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!input.contains(e.target) && !suggestionsEl.contains(e.target)) {
      hideSuggestions();
    }
  });

  function updateHighlight() {
    suggestionsEl.querySelectorAll('.city-suggestion').forEach((el, i) => {
      el.classList.toggle('highlighted', i === highlightIndex);
    });
  }

  function selectResult(index) {
    const result = currentResults[index];
    if (!result) return;

    const parts = result.displayName.split(', ');
    input.value = parts.slice(0, 2).join(', ');
    hideSuggestions();
    onSelect(result);
  }

  function hideSuggestions() {
    suggestionsEl.classList.remove('active');
    suggestionsEl.innerHTML = '';
    currentResults = [];
    highlightIndex = -1;
  }
}

/* =========== Timezone Resolution =========== */

async function resolveTimezone(lat, lon, infoId, coordsId, tzId, offsetHiddenId, ianaHiddenId) {
  const infoEl = document.getElementById(infoId);
  const coordsEl = document.getElementById(coordsId);
  const tzEl = document.getElementById(tzId);

  infoEl.style.display = 'block';
  coordsEl.textContent = `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
  tzEl.textContent = 'Ermittle Zeitzone…';

  try {
    const ianaTimezone = await getTimezoneFromCoords(lat, lon);
    document.getElementById(ianaHiddenId).value = ianaTimezone;

    // Calculate offset for the current date fields (will be recalculated on submit)
    const dateStr = document.getElementById('birthDate').value;
    const timeStr = document.getElementById('birthTime').value;

    if (dateStr && timeStr) {
      const offset = getUtcOffsetForDate(dateStr, timeStr, ianaTimezone);
      document.getElementById(offsetHiddenId).value = offset;
      tzEl.textContent = getTimezoneDescription(dateStr, timeStr, ianaTimezone) + ` — ${ianaTimezone}`;
    } else {
      tzEl.textContent = ianaTimezone;
      document.getElementById(offsetHiddenId).value = '';
    }
  } catch (err) {
    console.warn('Timezone resolution failed:', err);
    tzEl.textContent = 'Nicht ermittelt — bitte Datum eingeben und erneut versuchen';
  }
}

async function resolveTimezoneSimple(lat, lon, infoId, tzDisplayId, offsetHiddenId, ianaHiddenId) {
  const infoEl = document.getElementById(infoId);
  const tzEl = document.getElementById(tzDisplayId);

  infoEl.style.display = 'block';
  tzEl.textContent = 'Ermittle…';

  try {
    const ianaTimezone = await getTimezoneFromCoords(lat, lon);
    document.getElementById(ianaHiddenId).value = ianaTimezone;
    tzEl.textContent = ianaTimezone;
    // Offset will be calculated at submit time
  } catch {
    tzEl.textContent = 'Fehler';
  }
}

/* =========== Main chart =========== */

async function handleCalculate(e) {
  e.preventDefault();

  const date = document.getElementById('birthDate').value;
  const time = document.getElementById('birthTime').value;
  const ianaTimezone = document.getElementById('ianaTimezone').value;

  if (!date || !time) {
    alert('Bitte Datum und Uhrzeit ausfüllen.');
    return;
  }

  if (!ianaTimezone) {
    alert('Bitte zuerst einen Geburtsort auswählen (aus der Vorschlagsliste).');
    return;
  }

  const submitBtn = document.getElementById('submitBtn');
  submitBtn.textContent = 'Berechne…';
  submitBtn.disabled = true;

  try {
    // Calculate exact UTC offset for this specific date/time/timezone (handles DST)
    const offset = getUtcOffsetForDate(date, time, ianaTimezone);
    document.getElementById('timezone').value = offset;

    // Update timezone display
    const tzDisplay = document.getElementById('geoTimezone');
    if (tzDisplay) {
      tzDisplay.textContent = getTimezoneDescription(date, time, ianaTimezone) + ` — ${ianaTimezone}`;
    }

    document.getElementById('noChartMessage').style.display = 'block';
    document.getElementById('chartDetails').style.display = 'none';

    chart1 = calculateChart(date, time, offset.toString());
    if (!chart1.success) throw new Error(chart1.error);

    displayDetails(chart1);

    document.getElementById('noChartMessage').style.display = 'none';
    document.getElementById('chartDetails').style.display = 'block';
    document.getElementById('chartInfo').style.display = 'block';

    // Load extended astrology data async (Ascendant, Chiron, etc.)
    const lat = parseFloat(document.getElementById('latitude').value);
    const lon = parseFloat(document.getElementById('longitude').value);
    if (!isNaN(lat) && !isNaN(lon)) {
      setHtml('astroExtras', '<div class="astro-loading">Lade Astrologie-Daten (Swiss Ephemeris)…</div>');
      try {
        chart1 = await calculateAstrologyExtras(chart1, lat, lon);
        displayAstroExtras(chart1);
        // Refresh planet tables with retrograde + zodiac info
        setHtml('personalityPlanets', planetTable(chart1.personalityActivations));
        setHtml('designPlanets', planetTable(chart1.designActivations));
      } catch (astroErr) {
        console.warn('Astrology extras not available:', astroErr);
        setHtml('astroExtras', '<div class="astro-loading">Swiss Ephemeris nicht verfügbar — Basisdaten werden angezeigt.</div>');
      }
    }
  } catch (err) {
    document.getElementById('noChartMessage').style.display = 'block';
    console.error(err);
    alert('Fehler: ' + err.message);
  } finally {
    submitBtn.textContent = 'Chart berechnen';
    submitBtn.disabled = false;
  }
}

/* =========== Display =========== */

function displayDetails(c) {
  setText('detailType', c.type);
  setText('detailProfile', c.profile);
  setText('detailAuthority', c.authority);
  setText('detailDefinition', c.definition);

  // Incarnation Cross
  const crossText = c.incarnationCross
    ? c.incarnationCross.filter(Boolean).join(' / ')
    : '—';
  setText('detailIncarnation', crossText);

  // Variable
  if (c.variable) {
    setText('detailVariable', c.variable.arrows.join(' '));
  }

  // Sun sign + Chinese zodiac (available immediately, no SwissEph needed)
  if (c.sunSign) {
    setText('detailSunSign', `${c.sunSign.symbol} ${c.sunSign.name}`);
  }
  if (c.chineseZodiac) {
    setText('detailChineseZodiac', c.chineseZodiac.formatted);
  }

  // Dates
  setText('detailBirthDate', c.birthDate || '');
  setText('detailDesignDate', c.designDate || '');

  // Centers
  setHtml('definedCenters',
    c.definedCenters.map(center =>
      `<span class="badge">${CENTER_NAMES[center] || center}</span>`
    ).join(' ') || 'Keine'
  );

  // Channels
  setHtml('channels',
    c.definedChannels.map(([a, b]) =>
      `<div class="channel-item">${a}–${b}: ${getChannelName(a, b)}</div>`
    ).join('') || 'Keine'
  );

  // Gates
  setHtml('gates',
    c.allGateNums.sort((a, b) => a - b).map(gate => {
      const inP = c.personalityGateNums.includes(gate);
      const inD = c.designGateNums.includes(gate);
      const cls = inP && inD ? 'both' : inP ? 'personality' : 'design';
      return `<span class="gate-badge ${cls}" title="Tor ${gate}: ${GATE_NAMES[gate] || ''}">${gate}</span>`;
    }).join(' ')
  );

  // Planets
  setHtml('personalityPlanets', planetTable(c.personalityActivations));
  setHtml('designPlanets', planetTable(c.designActivations));
}

function planetTable(acts) {
  if (!acts || !acts.length) return '<em>Keine Daten</em>';
  const hasZodiac = acts.some(a => a.zodiac);
  return `<table class="planet-table">
    <thead><tr>
      <th>Planet</th>
      ${hasZodiac ? '<th>Zeichen</th>' : ''}
      <th>°</th>
      <th>Tor</th>
      <th>Linie</th>
      <th>F.T.B</th>
    </tr></thead>
    <tbody>${acts.map(a => {
      const retroMark = a.retrograde ? ' <span class="retro-mark" title="Retrograd">℞</span>' : '';
      const zodiacCol = hasZodiac
        ? `<td class="zodiac-col">${a.zodiac ? `${a.zodiac.symbol} ${a.zodiac.degree}°` : ''}${retroMark}</td>`
        : '';
      return `<tr>
        <td>${a.planetName || a.planet}${!hasZodiac && a.retrograde ? retroMark : ''}</td>
        ${zodiacCol}
        <td>${a.longitude.toFixed(2)}</td>
        <td>${a.gate}</td>
        <td>${a.line}</td>
        <td>${a.color || ''}.${a.tone || ''}.${a.base || ''}</td>
      </tr>`;
    }).join('')}
    </tbody></table>`;
}

/* =========== Astrology Extras Display =========== */

function displayAstroExtras(c) {
  if (!c.astroLoaded) {
    setHtml('astroExtras', '');
    return;
  }

  let html = '<div class="astro-grid">';

  // Ascendant
  if (c.ascendant) {
    html += `<div class="astro-card">
      <div class="astro-card-label">Aszendent</div>
      <div class="astro-card-value">${c.ascendant.zodiac.symbol} ${c.ascendant.zodiac.name}</div>
      <div class="astro-card-detail">${c.ascendant.zodiac.degree}°${c.ascendant.zodiac.minute.toString().padStart(2,'0')}' — ${c.ascendant.zodiac.element}</div>
    </div>`;
  }

  // MC
  if (c.mc) {
    html += `<div class="astro-card">
      <div class="astro-card-label">MC (Medium Coeli)</div>
      <div class="astro-card-value">${c.mc.zodiac.symbol} ${c.mc.zodiac.name}</div>
      <div class="astro-card-detail">${c.mc.zodiac.degree}°${c.mc.zodiac.minute.toString().padStart(2,'0')}' — ${c.mc.zodiac.element}</div>
    </div>`;
  }

  // Chiron
  if (c.chiron) {
    const chGate = getGateAndLine(c.chiron.longitude);
    html += `<div class="astro-card">
      <div class="astro-card-label">Chiron${c.chiron.retrograde ? ' ℞' : ''}</div>
      <div class="astro-card-value">${c.chiron.zodiac.symbol} ${c.chiron.zodiac.name} ${c.chiron.zodiac.degree}°</div>
      <div class="astro-card-detail">Tor ${chGate.gate}.${chGate.line} — ${GATE_NAMES[chGate.gate] || ''}</div>
    </div>`;
  }

  // Lilith
  if (c.lilith) {
    const liGate = getGateAndLine(c.lilith.longitude);
    html += `<div class="astro-card">
      <div class="astro-card-label">Lilith (Black Moon)</div>
      <div class="astro-card-value">${c.lilith.zodiac.symbol} ${c.lilith.zodiac.name} ${c.lilith.zodiac.degree}°</div>
      <div class="astro-card-detail">Tor ${liGate.gate}.${liGate.line} — ${GATE_NAMES[liGate.gate] || ''}</div>
    </div>`;
  }

  html += '</div>';

  // Retrograde summary
  if (c.planetRetrogrades) {
    const retros = Object.entries(c.planetRetrogrades)
      .filter(([_, v]) => v.retrograde)
      .map(([name]) => name);
    if (retros.length > 0) {
      html += `<div class="retro-summary">℞ Retrograd: ${retros.join(', ')}</div>`;
    }
  }

  setHtml('astroExtras', html);
}

/* =========== Composite =========== */

function toggleComposite() {
  const s = document.getElementById('compositeSection');
  if (s) s.style.display = s.style.display === 'none' ? 'block' : 'none';
}

async function handleComposite() {
  const d1 = document.getElementById('compositeBirthDate1').value;
  const t1 = document.getElementById('compositeBirthTime1').value;
  const iana1 = document.getElementById('compositeIanaTimezone1').value;
  const d2 = document.getElementById('compositeBirthDate2').value;
  const t2 = document.getElementById('compositeBirthTime2').value;
  const iana2 = document.getElementById('compositeIanaTimezone2').value;

  if (!d1 || !t1 || !iana1 || !d2 || !t2 || !iana2) {
    alert('Bitte alle Felder für beide Personen ausfüllen und Geburtsorte aus der Vorschlagsliste wählen.');
    return;
  }

  try {
    // Calculate UTC offsets for each person's specific date
    const offset1 = getUtcOffsetForDate(d1, t1, iana1);
    const offset2 = getUtcOffsetForDate(d2, t2, iana2);

    const c1 = calculateChart(d1, t1, offset1.toString());
    const c2 = calculateChart(d2, t2, offset2.toString());
    if (!c1.success || !c2.success) throw new Error('Chart-Berechnung fehlgeschlagen');

    // All gates from both people
    const allGates = [...new Set([...c1.allGateNums, ...c2.allGateNums])];

    // Channels completed in composite
    const compositeChannels = CHANNELS.filter(([gA, gB]) =>
      allGates.includes(gA) && allGates.includes(gB)
    );

    // Electromagnetic connections (each person brings one side)
    const emConnections = compositeChannels.filter(([gA, gB]) => {
      const a1 = c1.allGateNums.includes(gA), a2 = c2.allGateNums.includes(gA);
      const b1 = c1.allGateNums.includes(gB), b2 = c2.allGateNums.includes(gB);
      return (a1 && !a2 && b2 && !b1) || (a2 && !a1 && b1 && !b2);
    });

    // Compromise channels (both people have the same channel defined)
    const compromiseChannels = compositeChannels.filter(([gA, gB]) =>
      c1.definedChannels.some(([a, b]) => (a === gA && b === gB) || (a === gB && b === gA)) &&
      c2.definedChannels.some(([a, b]) => (a === gA && b === gB) || (a === gGB && b === gA))
    );

    const resultsDiv = document.getElementById('compositeResults');
    resultsDiv.innerHTML = `
      <h3>Composite-Analyse</h3>
      <div class="composite-overview">
        <div><strong>Person 1:</strong> ${c1.type} ${c1.profile} — ${c1.authority}</div>
        <div><strong>Person 2:</strong> ${c2.type} ${c2.profile} — ${c2.authority}</div>
      </div>

      <h4>Elektromagnetische Verbindungen (${emConnections.length})</h4>
      ${emConnections.length
        ? emConnections.map(([a, b]) => `<div class="channel-item">${a}–${b}: ${getChannelName(a, b)}</div>`).join('')
        : '<p>Keine</p>'
      }

      <h4>Kompromiss-Kanäle (${compromiseChannels.length})</h4>
      ${compromiseChannels.length
        ? compromiseChannels.map(([a, b]) => `<div class="channel-item">${a}–${b}: ${getChannelName(a, b)}</div>`).join('')
        : '<p>Keine</p>'
      }

      <h4>Alle Composite-Kanäle (${compositeChannels.length})</h4>
      ${compositeChannels.map(([a, b]) => `<div class="channel-item">${a}–${b}: ${getChannelName(a, b)}</div>`).join('')}
    `;
    resultsDiv.style.display = 'block';
  } catch (err) {
    console.error(err);
    alert('Composite-Fehler: ' + err.message);
  }
}

/* =========== Helpers =========== */
function setText(id, t) { const el = document.getElementById(id); if (el) el.textContent = t; }
function setHtml(id, h) { const el = document.getElementById(id); if (el) el.innerHTML = h; }
